import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { useFilteredArticles } from '../hooks/useFilteredArticles';
import { useArticleFilters } from '../hooks/useArticleFilters';
import { 
  CircularProgress, 
  TextField, 
  Button, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Chip,
  Box,
  Autocomplete,
  Paper,
  ListItem,
  ListItemText,
  Typography,
  Badge,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { 
  FilterList as FilterIcon, 
  Close as CloseIcon,
  ViewList as ViewListIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { FixedSizeList } from 'react-window';
import { Article, FilterRule, FilterCondition } from '../types/api.types';

interface ArticleSelectorProps {
  onSelectionChange: (filters: FilterRule[]) => void;
  onApplyFilters: (filters: FilterRule[]) => void;
  selectedFilters?: FilterRule[] | Record<string, any>; // Support both new and old format for backward compatibility
  onArticlesChange?: (articles: Article[]) => void;
}

// Available filter conditions
const FILTER_CONDITIONS: FilterCondition[] = [
  'equals',
  'contains',
  'different_from',
  'greater_than',
  'less_than',
  'greater_or_equal',
  'less_or_equal'
];

// Condition labels in French
const CONDITION_LABELS: Record<FilterCondition, string> = {
  equals: 'Égal à',
  contains: 'Contient',
  different_from: 'Différent de',
  greater_than: 'Supérieur à',
  less_than: 'Inférieur à',
  greater_or_equal: 'Supérieur ou égal à',
  less_or_equal: 'Inférieur ou égal à'
};

// Element labels in French (for display purposes)
const ELEMENT_LABELS: Record<string, string> = {
  'Article ID': 'Référence Article',
  'Article Name': 'Nom Article',
  'Stock actuel': 'Stock actuel'
};

// Helper function to normalize filters to new format
const normalizeFilters = (filters: FilterRule[] | Record<string, any> | undefined): FilterRule[] => {
  if (!filters) return [];
  if (Array.isArray(filters)) return filters;
  // Convert old format to new format (for backward compatibility)
  return Object.entries(filters)
    .filter(([key, value]) => key !== 'search' && value !== '' && value !== null && value !== undefined)
    .map(([key, value]) => ({
      element: key,
      condition: 'equals' as FilterCondition,
      value: Array.isArray(value) ? value[0] : value
    }));
};

const ArticleSelector: React.FC<ArticleSelectorProps> = ({ 
  onSelectionChange, 
  onApplyFilters,
  selectedFilters,
  onArticlesChange
}) => {
  const { activeDatabase } = useDatabaseContext();
  const normalizedSelectedFilters = useMemo(() => normalizeFilters(selectedFilters), [selectedFilters]);
  const [tempFilters, setTempFilters] = useState<FilterRule[]>(normalizedSelectedFilters);
  const [appliedFilters, setAppliedFilters] = useState<FilterRule[]>(normalizedSelectedFilters);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedSetDialogOpen, setSelectedSetDialogOpen] = useState(false);
  const [showEmptyResultsPopup, setShowEmptyResultsPopup] = useState(false);
  
  // Convert filters to format expected by API - only use appliedFilters (not tempFilters)
  const filtersForAPI = useMemo(() => {
    // Ensure appliedFilters is an array
    if (!Array.isArray(appliedFilters)) {
      return normalizeFilters(appliedFilters);
    }
    // Filter out empty filters
    return appliedFilters.filter(f => 
      f && f.element && f.condition && f.value !== '' && f.value !== null && f.value !== undefined
    );
  }, [appliedFilters]);
  
  // Récupérer les articles filtrés - only use applied filters
  const {
    data: filteredArticles,
    isLoading
  } = useFilteredArticles({
    databaseId: activeDatabase?.id || null,
    filters: filtersForAPI,
    search: '' // Search is now part of filters
  });

  // Fetch all articles for autocomplete (Article ID and Article Name dropdowns)
  // Only fetch when dialog is open to avoid unnecessary API calls
  const shouldFetchAllArticles = filterDialogOpen && !!activeDatabase?.id;
  const {
    data: allArticlesData,
    isLoading: isLoadingAllArticles
  } = useFilteredArticles({
    databaseId: shouldFetchAllArticles ? activeDatabase.id : null,
    filters: [],
    search: '',
    sortOptions: { sort_by: 'id', sort_direction: 'asc' }
  });

  const allArticles = allArticlesData?.articles || [];

  // Notify parent of articles change
  const prevArticlesRef = useRef<Article[] | undefined>(undefined);
  const prevFiltersRef = useRef<string>('');
  
  useEffect(() => {
    if (
      onArticlesChange &&
      filteredArticles?.articles &&
      filteredArticles.articles !== prevArticlesRef.current
    ) {
      prevArticlesRef.current = filteredArticles.articles;
      onArticlesChange(filteredArticles.articles);
    }
  }, [filteredArticles, onArticlesChange]);

  // Nombre d'articles dans l'ensemble sélectionné
  const selectedCount = filteredArticles?.articles?.length || 0;
  
  // Show popup when filters return empty results
  useEffect(() => {
    // Only show popup if:
    // - Filters are applied (not empty)
    // - Not loading
    // - Results are empty
    // - We have data (not initial state)
    // - Popup hasn't been shown for these filters yet
    const filtersKey = JSON.stringify(filtersForAPI);
    if (
      filtersForAPI.length > 0 &&
      !isLoading &&
      filteredArticles &&
      filteredArticles.articles &&
      filteredArticles.articles.length === 0 &&
      filtersKey !== prevFiltersRef.current
    ) {
      setShowEmptyResultsPopup(true);
      prevFiltersRef.current = filtersKey;
    }
    // Reset tracking when filters change (user applied new filters)
    if (filtersKey !== prevFiltersRef.current && showEmptyResultsPopup === false) {
      prevFiltersRef.current = '';
    }
  }, [filteredArticles, isLoading, filtersForAPI, showEmptyResultsPopup]);
  
  // Mettre à jour la sélection lorsque les filtres changent
  useEffect(() => {
    onSelectionChange(filtersForAPI);
  }, [filtersForAPI, onSelectionChange]);
  
  // Ouvrir/fermer la boîte de dialogue des filtres
  const toggleFilterDialog = () => {
    setFilterDialogOpen(!filterDialogOpen);
  };
  
  // Ouvrir/fermer la boîte de dialogue de l'ensemble sélectionné
  const toggleSelectedSetDialog = () => {
    setSelectedSetDialogOpen(!selectedSetDialogOpen);
  };
  
  // Appliquer les filtres temporaires
  const handleApplyFilters = () => {
    // Filter out empty filters
    const cleanedFilters = tempFilters.filter(f => 
      f.element && f.condition && f.value !== '' && f.value !== null && f.value !== undefined
    );
    setAppliedFilters(cleanedFilters);
    onApplyFilters(cleanedFilters);
    setFilterDialogOpen(false);
    // Reset popup tracking when new filters are applied
    prevFiltersRef.current = '';
  };

  // Ajouter un nouveau filtre
  const addFilter = () => {
    setTempFilters([...tempFilters, { element: '', condition: 'equals', value: '' }]);
  };

  // Mettre à jour un filtre temporaire
  const updateFilter = (index: number, field: keyof FilterRule, value: any) => {
    setTempFilters(prevFilters => {
      const newFilters = [...prevFilters];
      // Ensure the filter object exists
      if (!newFilters[index]) {
        newFilters[index] = { element: '', condition: 'equals', value: '' };
      }
      newFilters[index] = { ...newFilters[index], [field]: value };
      return newFilters;
    });
  };

  // Supprimer un filtre temporaire
  const removeFilter = (index: number) => {
    setTempFilters(tempFilters.filter((_, i) => i !== index));
  };

  // Supprimer un filtre appliqué
  const removeAppliedFilter = (index: number) => {
    const newFilters = appliedFilters.filter((_, i) => i !== index);
    setAppliedFilters(newFilters);
    onApplyFilters(newFilters);
  };

  // Réinitialiser les filtres temporaires
  const resetFilters = () => {
    setTempFilters([]);
  };

  // Reset temp filters when dialog opens to match applied filters
  useEffect(() => {
    if (filterDialogOpen) {
      setTempFilters([...appliedFilters]);
    }
  }, [filterDialogOpen, appliedFilters]);

  // Récupérer les options de filtres disponibles
  const { data: filterOptions, isLoading: isLoadingFilters } = useArticleFilters(activeDatabase?.id || null);
  
  // Get available elements (columns) for filtering - include special search elements
  const availableElements = useMemo(() => {
    const specialElements = ['Article ID', 'Article Name']; // Special elements for search
    if (!filterOptions) return specialElements;
    const categorical = filterOptions.columns?.categorical || [];
    const numeric = filterOptions.columns?.numeric || [];
    return [...specialElements, ...categorical, ...numeric];
  }, [filterOptions]);

  // Get available values for a specific element
  const getElementValues = (element: string): (string | number)[] => {
    if (!filterOptions || !element) return [];
    
    // Special elements use allArticles for autocompletion
    if (element === 'Article ID' || element === 'Article Name') {
      return [];
    }
    
    // Check if filterOptions has predefined values
    const filterValues = filterOptions.filters[element];
    if (Array.isArray(filterValues)) {
      return filterValues;
    }
    
    // If no predefined values, extract unique values from allArticles for autocompletion
    if (allArticles.length > 0 && element) {
      const uniqueValues = new Set<string | number>();
      allArticles.forEach(article => {
        const value = (article as any)[element];
        if (value !== null && value !== undefined && value !== '') {
          uniqueValues.add(value);
        }
      });
      return Array.from(uniqueValues).sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
      });
    }
    
    return [];
  };

  // Check if element is numeric
  const isNumericElement = (element: string): boolean => {
    if (!filterOptions) return false;
    // Special elements are text
    if (element === 'Article ID' || element === 'Article Name') {
      return false;
    }
    return (filterOptions.columns?.numeric || []).includes(element);
  };

  // Check if element should show dropdown for values
  // Use dropdown if there are predefined values from filterOptions
  // Otherwise use autocomplete for better UX
  const shouldShowValueDropdown = (element: string): boolean => {
    if (!element || !filterOptions) return false;
    
    // Special elements always use autocomplete
    if (element === 'Article ID' || element === 'Article Name') {
      return false;
    }
    
    // Check if filterOptions has predefined values (not from allArticles)
    const filterValues = filterOptions.filters[element];
    if (Array.isArray(filterValues) && filterValues.length > 0) {
      return true; // Use dropdown for predefined values
    }
    
    // For other elements, use autocomplete with values extracted from allArticles
    return false;
  };

  // Get available conditions for an element
  const getAvailableConditions = (element: string): FilterCondition[] => {
    if (!element) return FILTER_CONDITIONS;
    if (isNumericElement(element)) {
      return ['equals', 'different_from', 'greater_than', 'less_than', 'greater_or_equal', 'less_or_equal'];
    }
    return ['equals', 'contains', 'different_from'];
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" style={{ width: '600px', padding: '1rem' }}>
      {/* Empty results popup */}
      {showEmptyResultsPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEmptyResultsPopup(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Information
                </h3>
                <p className="text-sm text-gray-700">
                  Aucun article ne correspond aux critères sélectionnés.
                </p>
              </div>
              <button
                onClick={() => setShowEmptyResultsPopup(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowEmptyResultsPopup(false)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        width: '100%',
        height: '40px',
        overflow: 'hidden'
      }}>
        <IconButton 
          onClick={toggleFilterDialog}
          color="primary"
          sx={{ mr: 1, flexShrink: 0 }}
        >
          <FilterIcon />
        </IconButton>

        <IconButton 
          onClick={toggleSelectedSetDialog} 
          color="primary"
          sx={{ mr: 1, flexShrink: 0 }}
        >
          <Badge badgeContent={selectedCount} color="primary">
            <ViewListIcon />
          </Badge>
        </IconButton>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          overflow: 'auto',
          flexGrow: 1,
        }}>
          <Typography variant="subtitle1" sx={{ mr: 1, flexShrink: 0 }}>
            Filtres:
          </Typography>
          
          {appliedFilters.map((filter, index) => {
            const label = String(filter.value || '');
            return (
              <Chip
                key={index}
                label={label}
                onDelete={() => removeAppliedFilter(index)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  maxWidth: 200,
                  mr: 0.5,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>
      
      {/* Boîte de dialogue des filtres */}
      <Dialog open={filterDialogOpen} onClose={toggleFilterDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Filtres
          <IconButton
            aria-label="close"
            onClick={toggleFilterDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Filtres en trois colonnes */}
          {isLoadingFilters ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" fontWeight="bold">Élément</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" fontWeight="bold">Condition</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" fontWeight="bold">Valeur</Typography>
                </Grid>
                <Grid item xs={1}></Grid>
              </Grid>
              
              {tempFilters.map((filter, index) => {
                const availableConditions = getAvailableConditions(filter.element);
                const elementValues = getElementValues(filter.element);
                const isNumeric = isNumericElement(filter.element);
                
                return (
                  <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`element-label-${index}`}>Élément</InputLabel>
                        <Select
                          labelId={`element-label-${index}`}
                          value={filter?.element || ''}
                          label="Élément"
                          onChange={(e) => {
                            const newElement = e.target.value;
                            // Update element first
                            setTempFilters(prevFilters => {
                              const newFilters = [...prevFilters];
                              if (!newFilters[index]) {
                                newFilters[index] = { element: '', condition: 'equals', value: '' };
                              }
                              newFilters[index] = { 
                                ...newFilters[index], 
                                element: newElement,
                                // Reset condition and value when element changes
                                condition: (newElement === 'Article ID' || newElement === 'Article Name') 
                                  ? 'contains' 
                                  : (newElement === 'Stock actuel')
                                  ? 'equals' // Numeric condition for stock
                                  : 'equals',
                                value: ''
                              };
                              return newFilters;
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>Sélectionner un élément</em>
                          </MenuItem>
                          {availableElements.map((element) => (
                            <MenuItem key={element} value={element}>
                              {ELEMENT_LABELS[element] || element}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`condition-label-${index}`}>Condition</InputLabel>
                        <Select
                          labelId={`condition-label-${index}`}
                          value={filter?.condition || 'equals'}
                          label="Condition"
                          onChange={(e) => updateFilter(index, 'condition', e.target.value)}
                          disabled={!filter?.element}
                        >
                          {availableConditions.map((condition) => (
                            <MenuItem key={condition} value={condition}>
                              {CONDITION_LABELS[condition]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      {filter?.element === 'Stock actuel' ? (
                        // Stock filter: simple numeric input
                        <TextField
                          label="Valeur"
                          type="number"
                          value={filter?.value ?? ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : Number(e.target.value);
                            updateFilter(index, 'value', value);
                          }}
                          disabled={!filter?.element}
                          size="small"
                          fullWidth
                          inputProps={{
                            step: 'any'
                          }}
                        />
                      ) : filter?.element === 'Article ID' ? (
                        <Autocomplete
                          freeSolo
                          options={allArticles}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return String(option.id || '');
                          }}
                          value={(() => {
                            const currentValue = filter?.value ? String(filter.value) : '';
                            const matchedArticle = allArticles.find(a => String(a.id) === currentValue);
                            return matchedArticle || currentValue || null;
                          })()}
                          onChange={(_, newValue) => {
                            const value = typeof newValue === 'string' 
                              ? newValue 
                              : (newValue ? String(newValue.id) : '');
                            updateFilter(index, 'value', value);
                          }}
                          onInputChange={(_, newInputValue) => {
                            const currentValue = filter?.value ? String(filter.value) : '';
                            if (newInputValue !== currentValue) {
                              updateFilter(index, 'value', newInputValue);
                            }
                          }}
                          loading={isLoadingAllArticles}
                          disabled={!filter?.element}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Valeur"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingAllArticles ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      ) : filter?.element === 'Article Name' ? (
                        <Autocomplete
                          freeSolo
                          options={allArticles}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return String(option.name || option.id || '');
                          }}
                          value={(() => {
                            const currentValue = filter?.value ? String(filter.value) : '';
                            const matchedArticle = allArticles.find(a => String(a.name) === currentValue);
                            return matchedArticle || currentValue || null;
                          })()}
                          onChange={(_, newValue) => {
                            const value = typeof newValue === 'string' 
                              ? newValue 
                              : (newValue ? String(newValue.name || newValue.id) : '');
                            updateFilter(index, 'value', value);
                          }}
                          onInputChange={(_, newInputValue) => {
                            const currentValue = filter?.value ? String(filter.value) : '';
                            if (newInputValue !== currentValue) {
                              updateFilter(index, 'value', newInputValue);
                            }
                          }}
                          loading={isLoadingAllArticles}
                          disabled={!filter?.element}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Valeur"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingAllArticles ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      ) : shouldShowValueDropdown(filter?.element) ? (
                        <FormControl fullWidth size="small">
                          <InputLabel id={`value-label-${index}`}>Valeur</InputLabel>
                          <Select
                            labelId={`value-label-${index}`}
                            value={filter?.value ?? ''}
                            label="Valeur"
                            onChange={(e) => updateFilter(index, 'value', e.target.value)}
                            disabled={!filter?.element}
                          >
                            {elementValues.map((value) => (
                              <MenuItem key={value} value={value}>
                                {String(value)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        // Use Autocomplete for other fields with values extracted from allArticles
                        <Autocomplete
                          freeSolo
                          options={elementValues}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string' || typeof option === 'number') {
                              return String(option);
                            }
                            return String(option);
                          }}
                          value={filter?.value ?? null}
                          onChange={(_, newValue) => {
                            const value = typeof newValue === 'string' || typeof newValue === 'number'
                              ? newValue
                              : (newValue ? String(newValue) : '');
                            updateFilter(index, 'value', value);
                          }}
                          onInputChange={(_, newInputValue) => {
                            updateFilter(index, 'value', newInputValue);
                          }}
                          loading={isLoadingAllArticles}
                          disabled={!filter?.element}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Valeur"
                              type={isNumeric ? 'number' : 'text'}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingAllArticles ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        onClick={() => removeFilter(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}
              
              <Button
                startIcon={<AddIcon />}
                onClick={addFilter}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                + Filtre
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFilters}>Réinitialiser</Button>
          <Button onClick={handleApplyFilters} color="primary" variant="contained">
            Filtrer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Boîte de dialogue de l'ensemble sélectionné */}
      <Dialog 
        open={selectedSetDialogOpen} 
        onClose={toggleSelectedSetDialog} 
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Ensemble d'articles sélectionnés ({selectedCount})
          <IconButton
            aria-label="close"
            onClick={toggleSelectedSetDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : filteredArticles?.articles?.length && filteredArticles?.articles?.length > 0 ? (
            <FixedSizeList
              height={500}
              width="100%"
              itemCount={filteredArticles.articles.length}
              itemSize={40}
            >
              {({ index, style }) => {
                const article = filteredArticles.articles[index];
                return (
                  <Tooltip
                    key={article.id}
                    title={
                      <Paper sx={{ p: 1 }}>
                        {Object.entries(article)
                          .filter(([key]) => !['id', '_id', '__v'].includes(key))
                          .map(([key, value]) => (
                            <Typography key={key} variant="body2">
                              <strong>{key}:</strong> {value?.toString() || 'N/A'}
                            </Typography>
                          ))}
                      </Paper>
                    }
                    placement="right"
                    arrow
                  >
                    <ListItem style={style} divider>
                      <ListItemText
                        primary={
                          <Typography variant="body2" component="span">
                            <strong>{article.id}</strong> - {article.name || article.id}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </Tooltip>
                );
              }}
            </FixedSizeList>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              Aucun article ne correspond aux critères sélectionnés
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleSelectedSetDialog} color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ArticleSelector;
